import {
  AlertOctagon,
  CheckCircle2,
  ChevronRight,
  Clock,
  PlayCircle,
  RotateCcw,
  Search,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@/shared/lib/cn";
import { formatInteger } from "@/shared/lib/format";
import { Input } from "@/shared/ui";
import { KpiCard, type KpiItem } from "@/shared/ui/core";
import type { DetailSummaryMetric } from "@/shared/ui/patterns/DetailSummaryStrip";
import {
  MOBILE_KPI_TILE_STYLES,
  ensureUniqueKpiTones,
  resolveKpiTone,
} from "@/shared/ui/core/statusTone";

export type FieldTodayKpiStripProps = {
  metrics: DetailSummaryMetric[];
  activeId?: string | null;
  onSelect: (metricId: string) => void;
  /** Khi có, hiện thêm mục Tất cả ở đầu hàng. */
  onSelectAll?: () => void;
};

function getFieldKpiTone(metric: { id?: string; label?: string; tone?: any }) {
  const semanticTone = resolveKpiTone({
    id: metric.id,
    label: metric.label,
    tone: metric.tone,
  });
  const style = MOBILE_KPI_TILE_STYLES[semanticTone] ?? MOBILE_KPI_TILE_STYLES.neutral;
  return {
    semanticTone,
    ...style,
  };
}

function StatusTrack({
  metrics,
  activeId,
  onSelect,
  onSelectAll,
}: FieldTodayKpiStripProps) {
  if (metrics.length === 0) return null;
  const selected = activeId || (onSelectAll ? "all" : "");
  const allCount = metrics.reduce((sum, metric) => sum + (Number(metric.value) || 0), 0);

  return (
    <div
      role="tablist"
      aria-label="Lọc trạng thái"
      className="-mx-1 flex items-center gap-1.5 overflow-x-auto px-1 py-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
    >
      {onSelectAll ? (
        <button
          type="button"
          role="tab"
          aria-selected={selected === "all"}
          onClick={() => onSelectAll?.()}
          className={cn(
            "inline-flex shrink-0 cursor-pointer select-none items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-[12px] font-semibold transition-all duration-150 active:scale-95",
            "outline-none focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
            selected === "all"
              ? "border-primary bg-primary text-white shadow-xs"
              : "border-border/80 bg-surface text-text-secondary hover:bg-bg hover:text-text",
          )}
        >
          <span className={selected === "all" ? "font-bold text-white" : "text-text"}>
            Tất cả
          </span>
          <span
            className={cn(
              "inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1.5 text-[10px] font-bold tabular-nums",
              selected === "all"
                ? "bg-white/25 text-white"
                : "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
            )}
          >
            {formatInteger(allCount)}
          </span>
        </button>
      ) : null}
      {metrics.map((m) => {
        const tone = getFieldKpiTone(m);
        const active = selected === m.id;
        return (
          <button
            key={m.id}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onSelect(m.id)}
            className={cn(
              "inline-flex shrink-0 cursor-pointer select-none items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-[12px] font-semibold transition-all duration-150 active:scale-95",
              "outline-none focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
              active
                ? "border-primary bg-primary text-white shadow-xs"
                : "border-border/80 bg-surface text-text-secondary hover:bg-bg hover:text-text",
            )}
          >
            <span className={active ? "font-bold text-white" : "text-text"}>
              {m.label}
            </span>
            <span
              className={cn(
                "inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1.5 text-[10px] font-bold tabular-nums",
                active ? "bg-white/25 text-white" : tone.badge,
              )}
            >
              {typeof m.value === "number" ? formatInteger(m.value) : m.value}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/** Ô thống kê trên trang chủ: Ma trận 2x2 cân đối (4 chỉ số trọng yếu, đồng bộ với /exe). */
export function FieldTodayKpiStrip({
  metrics,
  activeId,
  onSelect,
  hrefFor,
}: FieldTodayKpiStripProps & {
  hrefFor?: (metricId: string) => string;
}) {
  if (metrics.length === 0) return null;

  const metricMap = new Map<string, number>();
  for (const m of metrics) {
    metricMap.set(m.id, Number(m.value) || 0);
  }

  const pendingVal = metricMap.get("pending") ?? 0;
  const inProgressVal = metricMap.get("inProgress") ?? 0;
  const pausedVal = metricMap.get("paused") ?? 0;
  const blockedVal = metricMap.get("blocked") ?? 0;
  const cancelledVal = metricMap.get("cancelled") ?? 0;
  const completedVal = metricMap.get("completed") ?? 0;

  const runningTotal = inProgressVal + pausedVal;
  const blockedTotal = blockedVal + cancelledVal;

  const rawTiles: Array<{
    id: string;
    label: string;
    value: string;
    hint?: string;
    tone: "draft" | "info" | "danger" | "success";
    icon: LucideIcon;
  }> = [
    {
      id: "inProgress",
      label: "Đang chạy",
      value: formatInteger(runningTotal),
      hint: pausedVal > 0 ? `${formatInteger(pausedVal)} tạm dừng` : "Trên dây chuyền",
      tone: "info",
      icon: PlayCircle,
    },
    {
      id: "pending",
      label: "Chờ thực hiện",
      value: formatInteger(pendingVal),
      hint: "Sẵn sàng nhận việc",
      tone: "draft",
      icon: Clock,
    },
    {
      id: "completed",
      label: "Hoàn tất",
      value: formatInteger(completedVal),
      hint: "Đã ghi nhận xong",
      tone: "success",
      icon: CheckCircle2,
    },
    {
      id: "blocked",
      label: "Tạm giữ",
      value: formatInteger(blockedTotal),
      hint:
        cancelledVal > 0
          ? `${formatInteger(cancelledVal)} đã hủy`
          : blockedTotal > 0
            ? "Cần xử lý mở khóa"
            : "Không bị chặn",
      tone: "danger",
      icon: AlertOctagon,
    },
  ];

  const tiles = ensureUniqueKpiTones(rawTiles);

  return (
    <div
      className="grid grid-cols-2 gap-2.5 sm:grid-cols-4"
      role="group"
      aria-label="Thống kê công đoạn"
    >
      {tiles.map((tile) => {
        const href = hrefFor?.(tile.id);
        const Icon = tile.icon ?? ChevronRight;
        const isSelected =
          activeId === tile.id ||
          (tile.id === "inProgress" && activeId === "paused") ||
          (tile.id === "blocked" &&
            (activeId === "cancelled" || activeId === "skipped"));

        const item: KpiItem = {
          id: tile.id,
          label: tile.label,
          value: tile.value,
          unit: "công đoạn",
          hint: tile.hint,
          tone: tile.tone,
          icon: Icon,
          selected: isSelected,
          to: href,
          onClick: href ? undefined : () => onSelect(tile.id),
        };

        return <KpiCard key={tile.id} item={item} dense variant="surface" />;
      })}
    </div>
  );
}

export type FieldTodayQueueBarProps = FieldTodayKpiStripProps & {
  query: string;
  onQueryChange: (value: string) => void;
  placeholder?: string;
  onClear?: () => void;
};

/** Ô tìm và trạng thái trong một khung. */
export function FieldTodayQueueBar({
  query,
  onQueryChange,
  placeholder = "Lệnh sản xuất, mẻ hoặc công đoạn",
  onClear,
  ...status
}: FieldTodayQueueBarProps) {
  return (
    <section className="space-y-2 rounded-2xl border border-border/70 bg-surface p-3 shadow-xs">
      <div className="flex items-center gap-2">
        <Input
          aria-label="Tìm kiếm"
          size="sm"
          value={query}
          placeholder={placeholder}
          startAdornment={<Search className="size-4 text-text-muted" aria-hidden />}
          onChange={(e) => onQueryChange(e.target.value)}
          className="h-10 min-h-10 flex-1 rounded-xl text-body"
        />
        {onClear ? (
          <button
            type="button"
            onClick={onClear}
            className="inline-flex h-10 shrink-0 cursor-pointer items-center gap-1.5 rounded-xl px-2.5 text-[13px] font-semibold text-primary hover:bg-primary-soft/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-focus-ring)]"
          >
            <RotateCcw className="size-3.5" aria-hidden />
            Xóa lọc
          </button>
        ) : null}
      </div>
      <StatusTrack {...status} />
    </section>
  );
}
